import { QueryClient, QueryKey, dehydrate, hydrate, DehydratedState } from 'react-query';
import { get, set, del, createStore, UseStore } from 'idb-keyval';

import { MutationQueue } from './types';

/**
 * Custom store for idb-keyval to avoid conflicts and ensure proper initialization
 * Using a dedicated database for the Installers App offline functionality
 */
const DB_NAME = 'installers-app-offline-db';
const STORE_NAME = 'offline-store';
const DB_VERSION = 2; // Increment version to trigger upgrade and create the store

let offlineStore: UseStore;
let dbInitPromise: Promise<void> | null = null;

/**
 * Initialize the IndexedDB database with proper version handling
 * This ensures the object store is created even if the database already exists
 */
async function initializeOfflineStore(): Promise<void> {
  if (dbInitPromise) {
    return dbInitPromise;
  }

  dbInitPromise = new Promise<void>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      // Create the object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      db.close(); // Close the connection so idb-keyval can use it
      // Now create the idb-keyval store with the same database/store
      offlineStore = createStore(DB_NAME, STORE_NAME);
      resolve();
    };
  });

  return dbInitPromise;
}

/**
 * Get the offline store, initializing if needed
 * Exported for use by other modules that need to access the same IndexedDB store
 */
export async function getOfflineStore(): Promise<UseStore> {
  if (!offlineStore) {
    await initializeOfflineStore();
  }
  return offlineStore;
}

/**
 * Unified storage key for all offline data (queries + mutations)
 */
export const OFFLINE_STORAGE_KEY = 'RQ_CACHE_V1';

/**
 * Storage structure for offline data
 */
export type OfflineStorage = {
  queries?: unknown; // Dehydrated React Query state
  mutations?: MutationQueue;
};

/**
 * Get the full offline storage object
 */
export async function getOfflineStorage(): Promise<OfflineStorage> {
  try {
    const store = await getOfflineStore();
    const storage = await get<OfflineStorage>(OFFLINE_STORAGE_KEY, store);
    return storage ?? {};
  } catch {
    return {};
  }
}

/**
 * Save the full offline storage object
 */
export async function saveOfflineStorage(storage: OfflineStorage): Promise<void> {
  try {
    const store = await getOfflineStore();
    await set(OFFLINE_STORAGE_KEY, storage, store);
  } catch {
    // Failed to save - ignore
  }
}

export async function restoreQueryClient(queryClient: QueryClient) {
  const storage = await getOfflineStorage();
  if (storage.queries) hydrate(queryClient, storage.queries);
}

/**
 * Reset query cache to persisted snapshot without triggering refetches
 *
 * This function:
 * 1. Gets the persisted query data from IndexedDB
 * 2. Uses setQueryData to replace each query's data (doesn't trigger fetches)
 * 3. Removes queries that don't exist in the persisted snapshot
 *
 * Unlike clear() + hydrate(), this approach:
 * - Doesn't leave the cache empty between operations
 * - Doesn't trigger automatic refetches
 * - Works correctly even when the app is offline
 */
export async function resetQueryCacheToPersistedSnapshot(queryClient: QueryClient): Promise<void> {
  const storage = await getOfflineStorage();
  const dehydratedState = storage.queries as DehydratedState | undefined;

  // Get all current query keys in the cache
  const currentQueries = queryClient.getQueryCache().getAll();

  // Build a set of persisted query hashes for quick lookup
  const persistedQueries = dehydratedState?.queries ?? [];
  const persistedQueryHashes = new Set(persistedQueries.map((q) => q.queryHash));

  // Restore each persisted query's data using setQueryData (doesn't trigger fetch)
  persistedQueries.forEach((persistedQuery) => {
    // Only set data if the query was successful
    if (persistedQuery.state.status === 'success' && persistedQuery.state.data !== undefined) {
      queryClient.setQueryData(persistedQuery.queryKey, persistedQuery.state.data, {
        // Update the timestamp so React Query knows this is fresh data
        updatedAt: persistedQuery.state.dataUpdatedAt,
      });
    }
  });

  // Remove queries that exist in memory but not in persisted snapshot
  // These are "optimistic" queries created during offline mutations
  currentQueries.forEach((query) => {
    if (!persistedQueryHashes.has(query.queryHash)) {
      queryClient.removeQueries(query.queryKey, { exact: true });
    }
  });
}

export async function persistQueryClient(queryClient: QueryClient) {
  const dehydrated = dehydrate(queryClient, {
    shouldDehydrateQuery: (q) => q.state.status === 'success',
  });
  const storage = await getOfflineStorage();
  storage.queries = dehydrated;
  await saveOfflineStorage(storage);
}

export async function clearPersistedQueryClientCache() {
  const storage = await getOfflineStorage();
  delete storage.queries;
  await saveOfflineStorage(storage);
}

export function clearInMemoryQueryClientCache(queryClient: QueryClient) {
  queryClient.clear();
}

export async function clearQueryClientCache(queryClient: QueryClient) {
  await clearPersistedQueryClientCache();
  clearInMemoryQueryClientCache(queryClient);
}

/**
 * Clear all offline data (queries + mutations)
 * Use this on logout
 */
export async function clearAllOfflineData(queryClient?: QueryClient) {
  try {
    const store = await getOfflineStore();
    await del(OFFLINE_STORAGE_KEY, store);
  } catch {
    // Failed to clear - ignore
  }
  if (queryClient) {
    clearInMemoryQueryClientCache(queryClient);
  }
}

const keyToString = (key: QueryKey) => JSON.stringify(key);

export async function persistOnlyKeys(queryClient: QueryClient, allowedKeys: ReadonlyArray<QueryKey>) {
  const allowed = new Set(allowedKeys.map(keyToString));

  const dehydrated = dehydrate(queryClient, {
    shouldDehydrateQuery: (q) => q.state.status === 'success' && allowed.has(keyToString(q.queryKey)),
  });

  const storage = await getOfflineStorage();
  const existingState = storage.queries as DehydratedState | undefined;

  if (existingState?.queries?.length) {
    const newQueryHashes = new Set(dehydrated.queries.map((q) => q.queryHash));
    const preservedQueries = existingState.queries.filter((q) => !newQueryHashes.has(q.queryHash));
    dehydrated.queries = [...preservedQueries, ...dehydrated.queries];
  }

  storage.queries = dehydrated;
  await saveOfflineStorage(storage);
}
